import {Material} from '@hconnect/common/types'
import {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {
  useHistoryAssetsByIdQuery,
  useHistoryAssetsQuery,
  useMaterialsRecipesById,
  useMaterialsStockLevelsQuery,
  useMaterialStorageDataFromHistoryQuery
} from '../../../shared/hooks/api'
import {getMillProductionTimeForMaterial} from '../../../shared/selectors'
import {MomentRange} from '../../../shared/selectors/time'
import {usePlanningChartScheduleData} from '../../page-planning/dailyPlanning/PlanningChartScheduleDataProvider'
import {usePlanningChartStartEnd} from '../../page-planning/dailyPlanning/PlanningChartStartEndProvider'

type OptimizerStockMillETAIndicatorProps = {
  material: Material
}

export const OptimizerStockMillETAIndicator = ({material}: OptimizerStockMillETAIndicatorProps) => {
  const materialId = material.id

  const {
    i18n: {language}
  } = useTranslation()

  const {startOfPlan, endOfPlan, endOfChart} = usePlanningChartStartEnd()
  const planTimeFrame: MomentRange = [startOfPlan, endOfPlan]

  const {scheduleId} = usePlanningChartScheduleData()

  const {data: stockLevelsData} = useMaterialsStockLevelsQuery({
    pointAtTime: endOfChart,
    planRange: [startOfPlan, endOfPlan],
    scheduleId: scheduleId
  })

  const stockLevel = stockLevelsData ? stockLevelsData[materialId] : undefined

  const {data: recipesById} = useMaterialsRecipesById()
  const {data: assetsById} = useHistoryAssetsByIdQuery({
    timeFrame: planTimeFrame,
    throwOnError: false
  })
  const {data: assets} = useHistoryAssetsQuery({timeFrame: planTimeFrame, throwOnError: false})
  const {data: storageData} = useMaterialStorageDataFromHistoryQuery({
    materialId,
    timeFrame: planTimeFrame
  })

  const storageCapacity = storageData?.storageCapacity ?? 0

  const millETA = useMemo(() => {
    if (!recipesById || !assetsById || !assets || stockLevel === undefined) {
      return undefined
    }
    return getMillProductionTimeForMaterial({
      assetsById,
      assets,
      tonsOfMaterial: Math.max(0, storageCapacity - stockLevel),
      recipesById,
      materialId
    })
      .map(
        (production) =>
          `${production.asset_name}: ${Math.ceil(production.time).toLocaleString(language)}h`
      )
      .join(' ')
  }, [materialId, assetsById, assets, stockLevel, storageCapacity, recipesById, language])

  return <>{millETA}</>
}
